import api from '../utils/api'

class QRService {
    constructor() {
        this.frontendQRService = null; // Will be initialized when needed
    }

    /**
     * Initialize frontend QR generation service
     */
    async initializeFrontendGeneration() {
        if (this.frontendQRService) return this.frontendQRService;

        try {
            // Dynamically import qr-code-styling for frontend generation
            const QRCodeStyling = (await import('qr-code-styling')).default;
            this.frontendQRService = QRCodeStyling;
            return this.frontendQRService;
        } catch (error) {
            console.warn('Frontend QR generation not available:', error);
            return null;
        }
    }

    /**
     * Generate basic QR code (backend)
     */
    async generate(text, options = {}) {
        try {
            const response = await api.post('/qr-code/generate', {
                text,
                options: {
                    ...options,
                    source: 'backend',
                }
            });
            const data = response.data?.data || {};
            return {
                ...data,
                qrCode: data.qrCode,
                source: 'backend',
                immediate: true,
                saved: true
            };
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to generate QR code');
        }
    }

    /**
     * Generate styled QR code using frontend library (fast, interactive)
     */
    async generateStyledFrontend(text, style = {}, options = {}) {
        try {
            const QRCodeStyling = await this.initializeFrontendGeneration();
            if (!QRCodeStyling) {
                // Fallback to backend if frontend unavailable
                return this.generateStyled(text, style, options);
            }

            // Create QR with qr-code-styling
            const qrCode = new QRCodeStyling({
                width: options.size || options.width || 300,
                height: options.size || options.width || 300,
                type: options.type || "png",
                data: text,
                margin: options.margin || 10,
                qrOptions: {
                    errorCorrectionLevel: options.errorCorrectionLevel || "M"
                },
                dotsOptions: {
                    color: style.darkColor || options.darkColor || "#000000",
                    type: style.dotsType || "square"
                },
                backgroundOptions: {
                    color: style.lightColor || style.backgroundColor || options.lightColor || "#ffffff"
                },
                cornersSquareOptions: {
                    color: style.darkColor || options.darkColor || "#000000",
                    type: style.cornersSquareType || "square"
                },
                cornersDotOptions: {
                    color: style.darkColor || options.darkColor || "#000000",
                    type: style.cornersDotType || "square"
                }
            });

            // Handle gradients
            if (style.gradientColors && style.gradientColors.length > 0) {
                const gradientOptions = {
                    type: style.gradientType || "linear",
                    rotation: style.gradientRotation || 0,
                    colorStops: style.gradientColors.map((color, index) => ({
                        offset: index / (style.gradientColors.length - 1),
                        color: color
                    }))
                };

                qrCode.update({
                    dotsOptions: {
                        ...qrCode._options.dotsOptions,
                        gradient: gradientOptions
                    }
                });
            }

            // Get data URL for immediate use
            const dataUrl = await new Promise((resolve) => {
                qrCode.getRawData("png").then(buffer => {
                    const blob = new Blob([buffer], { type: 'image/png' });
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                });
            });

            // FIXED: Save to backend using the correct endpoint
            try {
                const saveResponse = await api.post('/qr-code/save', {
                    qrCodeData: dataUrl,        // ✅ Matches backend validation
                    content: text,              // ✅ Matches backend validation
                    contentType: options.contentType || this.detectContentType(text),
                    style,                      // ✅ Matches backend validation
                    metadata: {                 // ✅ Matches backend validation
                        width: options.size || options.width || 300,
                        height: options.size || options.width || 300,
                        margin: options.margin || 10,
                        errorCorrectionLevel: options.errorCorrectionLevel || 'M',
                        libraryVersion: 'qr-code-styling',
                        generationMethod: 'styled',
                        originalContent: options.originalContent || null,
                        hasLogo: false,
                        source: 'frontend'
                    }
                });

                return {
                    ...saveResponse.data,
                    qrCode: dataUrl,
                    source: 'frontend',
                    immediate: true,
                    saved: true
                };
            } catch (saveError) {
                console.warn('Failed to save to backend:', saveError);
                // Return frontend result even if backend save fails
                return {
                    qrCode: dataUrl,
                    source: 'frontend',
                    immediate: true,
                    saved: false
                };
            }

        } catch (error) {
            console.warn('Frontend generation failed, falling back to backend:', error);
            return this.generateStyled(text, style, options);
        }
    }

    async saveFrontendQR(qrCodeData, text, style = {}, options = {}) {

    }

    /**
     * Generate styled QR code. Backend has no /generate-styled endpoint; styled is frontend-only.
     */
    async generateStyled(text, style = {}, options = {}) {
        return this.generateStyledFrontend(text, style, options);
    }

    /**
     * Smart generation - chooses frontend or backend based on complexity
     */
    async generateSmart(text, style = {}, options = {}) {
        // Use frontend for simple styling (faster)
        const shouldUseFrontend = this.shouldUseFrontendGeneration(style, options);

        if (shouldUseFrontend) {
            return this.generateStyledFrontend(text, style, options);
        } else {
            return this.generateStyled(text, style, options);
        }
    }

    /**
     * Determine if frontend generation should be used
     */
    shouldUseFrontendGeneration(style = {}, options = {}) {
        // Use frontend for:
        // - Basic styling
        // - Gradient effects
        // - Standard dot types
        // - No logo/icon

        // Use backend for:
        // - Logo processing
        // - Complex custom styling
        // - Batch operations
        // - SVG generation

        if (options.hasLogo || options.logo) return false;
        if (options.format === 'svg') return false;
        if (style.iconStyle || style.logoScale) return false;

        // Frontend can handle these
        const frontendCapableFeatures = [
            'dotsType', 'cornersSquareType', 'cornersDotType',
            'gradientColors', 'gradientType', 'gradientRotation',
            'darkColor', 'lightColor', 'backgroundColor'
        ];

        const hasOnlyFrontendFeatures = Object.keys(style).every(key =>
            frontendCapableFeatures.includes(key) || ['type', 'preset'].includes(key)
        );

        return hasOnlyFrontendFeatures;
    }

    /**
     * Generate QR code with logo (always backend)
     */
    async generateWithLogo(text, logoFile, options = {}) {
        try {
            const formData = new FormData();
            formData.append('text', text);
            formData.append('options', JSON.stringify({
                ...options,
                source: 'backend'
            }));
            formData.append('logo', logoFile);

            const response = await api.post('/qr-code/generate-with-logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return {
                ...response.data,
                qrCode: response.data.data.qrCode,
                source: 'backend',
                immediate: true,
                saved: true
            };
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to generate QR code with logo');
        }
    }

    /**
     * Generate QR code with icon/logo. Backend has no /generate-styled endpoint;
     * falls back to generate-with-logo (style is not applied on backend).
     */
    async generateStyledWithIcon(text, iconFile, style = {}, options = {}) {
        return this.generateWithLogo(text, iconFile, options);
    }

    /**
     * Generate with preset (smart routing)
     */
    async generateWithPreset(text, preset, options = {}) {
        const presets = this.getPresetStyles();
        const style = presets[preset];

        if (!style) {
            throw new Error(`Unknown preset: ${preset}. Available: ${Object.keys(presets).join(', ')}`);
        }

        // Add preset info to style
        const enhancedStyle = {
            ...style,
            preset: preset
        };

        return this.generateSmart(text, enhancedStyle, options);
    }

    /**
     * Get preset style configurations
     */
    getPresetStyles() {
        return {
            modern: {
                dotsType: 'classy-rounded',
                darkColor: '#2563eb',
                lightColor: '#f8fafc',
                cornersSquareType: 'extra-rounded'
            },
            elegant: {
                gradientColors: ['#667eea', '#764ba2'],
                gradientType: 'linear',
                lightColor: '#ffffff'
            },
            minimal: {
                dotsType: 'dots',
                darkColor: '#1f2937',
                lightColor: '#ffffff'
            },
            vibrant: {
                gradientColors: ['#ff6b6b', '#4ecdc4'],
                gradientType: 'linear',
                lightColor: '#ffffff'
            },
            classic: {
                dotsType: 'square',
                darkColor: '#000000',
                lightColor: '#ffffff'
            },
            rounded: {
                dotsType: 'rounded',
                cornersSquareType: 'extra-rounded',
                darkColor: '#1f2937',
                lightColor: '#ffffff'
            },
            gradient_blue: {
                gradientColors: ['#3b82f6', '#1d4ed8'],
                gradientType: 'linear',
                gradientRotation: 45
            },
            gradient_purple: {
                gradientColors: ['#8b5cf6', '#7c3aed'],
                gradientType: 'radial'
            },
            professional: {
                dotsType: 'rounded',
                cornersSquareType: 'square',
                darkColor: '#1e40af',
                lightColor: '#f9fafb'
            },
            neon: {
                gradientColors: ['#00f5ff', '#ff00ff'],
                gradientType: 'linear',
                dotsType: 'dots'
            }
        };
    }

    /**
     * Legacy style generation methods (with smart routing)
     */
    async generateDotsStyle(text, options = {}) {
        const style = {
            dotsType: 'dots',
            darkColor: options.darkColor || '#000000',
            lightColor: options.lightColor || '#ffffff'
        };

        return this.generateSmart(text, style, options);
    }

    async generateGradientStyle(text, gradientColors = ['#FF6B6B', '#4ECDC4'], options = {}) {
        const style = {
            gradientColors,
            gradientType: 'linear',
            lightColor: options.lightColor || '#ffffff'
        };

        return this.generateSmart(text, style, options);
    }

    async generateRoundedStyle(text, cornerRadius = 3, options = {}) {
        const style = {
            dotsType: 'rounded',
            cornersSquareType: 'extra-rounded',
            darkColor: options.darkColor || '#000000',
            lightColor: options.lightColor || '#ffffff'
        };

        return this.generateSmart(text, style, options);
    }

    /**
     * Generate vCard QR code
     */
    async generateVCard(contact, options = {}, style = null) {
        try {
            const payload = {
                contact,
                options: {
                    ...options,
                    source: style ? 'frontend' : 'backend'
                }
            };

            if (style) {
                payload.style = style;
            }

            const response = await api.post('/qr-code/generate-vcard', payload, { timeout: 15000 });
            const { data } = response.data || {};
            return data ? { ...data, source: data.metadata?.source || 'backend' } : response.data;
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            if (status === 400 && data?.details && Array.isArray(data.details)) {
                const err = Object.assign(new Error(data.error || 'Validation failed'), { details: data.details });
                throw err;
            }
            throw new Error(data?.error || error.message || 'Failed to generate vCard QR code');
        }
    }

    /**
     * Generate WiFi QR code
     */
    async generateWiFi(wifi, options = {}, style = null) {
        try {
            const payload = {
                wifi,
                options: {
                    ...options,
                    source: style ? 'frontend' : 'backend'
                }
            };

            if (style) {
                payload.style = style;
            }

            const response = await api.post('/qr-code/generate-wifi', payload, { timeout: 15000 });
            const { data } = response.data || {};
            return data ? { ...data, source: data.metadata?.source || 'backend' } : response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Failed to generate WiFi QR code');
        }
    }

    /**
     * Generate batch QR codes (always backend)
     */
    async generateBatch(items, options = {}) {
        try {
            const response = await api.post('/qr-code/generate-batch', {
                items,
                options: {
                    ...options,
                    source: 'backend'
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to generate batch QR codes');
        }
    }

    /**
     * Generate frontend batch (for simple styling)
     */
    async generateFrontendBatch(items, options = {}) {
        try {
            const QRCodeStyling = await this.initializeFrontendGeneration();
            if (!QRCodeStyling) {
                return this.generateBatch(items, options);
            }

            const results = [];

            for (const item of items) {
                try {
                    const result = await this.generateStyledFrontend(
                        item.text,
                        item.style || {},
                        { ...options, ...item.options }
                    );
                    results.push(result);
                } catch (error) {
                    console.warn(`Failed to generate QR for item:`, item, error);
                    // Continue with other items
                }
            }

            return {
                qrCodes: results,
                source: 'frontend',
                total: results.length
            };
        } catch (error) {
            throw new Error('Failed to generate frontend batch QR codes');
        }
    }

    /**
     * Smart batch generation
     */
    async generateSmartBatch(items, options = {}) {
        // Check if all items can be handled by frontend
        const canUseFrontend = items.every(item =>
            this.shouldUseFrontendGeneration(item.style || {}, item.options || {})
        );

        if (canUseFrontend && items.length <= 10) {
            return this.generateFrontendBatch(items, options);
        } else {
            return this.generateBatch(items, options);
        }
    }

    /**
     * Get QR code by ID
     */
    async getQR(id) {
        try {
            const response = await api.get(`/qr-code/${id}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to retrieve QR code');
        }
    }

    /**
     * Get user's QR codes with enhanced filtering
     */
    async getMyQRs(filters = {}) {
        try {
            const params = {
                page: filters.page || 1,
                limit: filters.limit || 20
            };

            if (filters.contentType) params.contentType = filters.contentType;
            if (filters.hasStyle !== undefined) params.hasStyle = filters.hasStyle;
            if (filters.styleType) params.styleType = filters.styleType;
            if (filters.source) params.source = filters.source;
            if (filters.preset) params.preset = filters.preset;

            const response = await api.get('/qr-code/my-qrs', { params });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to retrieve QR codes');
        }
    }

    /**
     * Get enhanced analytics
     */
    async getAnalytics(timeframe = '30d', source = null) {
        try {
            const params = { timeframe };
            if (source) params.source = source;

            const response = await api.get('/qr-code/analytics/enhanced', { params });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to retrieve analytics');
        }
    }

    /**
     * Get usage analytics (frontend vs backend)
     */
    async getUsageAnalytics(timeframe = '30d') {
        try {
            const response = await api.get('/qr-code/analytics/usage', {
                params: { timeframe }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to retrieve usage analytics');
        }
    }

    /**
     * Download QR code
     */
    async downloadQR(id, format = 'png') {
        try {
            const response = await api.get(`/qr-code/${id}/download`, {
                params: { format },
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `qrcode-${id}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to download QR code');
        }
    }

    /**
     * Delete QR code
     */
    async deleteQR(id) {
        try {
            const response = await api.delete(`/qr-code/${id}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to delete QR code');
        }
    }

    /**
     * Update QR style (frontend-generated only)
     */
    async updateQRStyle(id, newStyle, options = {}) {
        try {
            const response = await api.put(`/qr-code/${id}/style`, {
                style: newStyle,
                options: {
                    ...options,
                    source: 'frontend'
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to update QR style');
        }
    }

    // Legacy methods for backward compatibility
    async getHistory(page = 1, limit = 20) {
        return this.getMyQRs({ page, limit });
    }

    async deleteFromHistory(id) {
        return this.deleteQR(id);
    }

    async getStyleAnalytics(timeframe = '30d') {
        return this.getAnalytics(timeframe);
    }

    /**
     * Validate style options
     */
    validateStyleOptions(style) {
        if (!style) return true;

        const validDotsTypes = ['square', 'dots', 'rounded', 'extra-rounded', 'classy', 'classy-rounded'];
        const validCornerTypes = ['square', 'extra-rounded', 'dot'];
        const validGradientTypes = ['linear', 'radial'];

        if (style.dotsType && !validDotsTypes.includes(style.dotsType)) {
            throw new Error(`Invalid dotsType. Must be one of: ${validDotsTypes.join(', ')}`);
        }

        if (style.cornersSquareType && !validCornerTypes.includes(style.cornersSquareType)) {
            throw new Error(`Invalid cornersSquareType. Must be one of: ${validCornerTypes.join(', ')}`);
        }

        if (style.gradientType && !validGradientTypes.includes(style.gradientType)) {
            throw new Error(`Invalid gradientType. Must be one of: ${validGradientTypes.join(', ')}`);
        }

        if (style.gradientColors && (!Array.isArray(style.gradientColors) || style.gradientColors.length < 2)) {
            throw new Error('gradientColors must be an array of at least 2 colors');
        }

        return true;
    }

    /**
     * Get available options
     */
    getAvailableOptions() {
        return {
            presets: Object.keys(this.getPresetStyles()),
            dotsTypes: ['square', 'dots', 'rounded', 'extra-rounded', 'classy', 'classy-rounded'],
            cornerTypes: ['square', 'extra-rounded', 'dot'],
            gradientTypes: ['linear', 'radial'],
            formats: ['png', 'jpeg', 'svg', 'webp'],
            sources: ['frontend', 'backend'],
            capabilities: {
                frontend: ['basic styling', 'gradients', 'dot patterns', 'instant generation'],
                backend: ['logo processing', 'complex styling', 'batch operations', 'SVG generation']
            }
        };
    }

    /**
     * Get service status
     * Uses GET /qr-code/health (backend QR health). Response must include status: 'healthy' or data.service: 'healthy'.
     */
    async getServiceStatus() {
        try {
            const frontendAvailable = await this.initializeFrontendGeneration() !== null;

            const backendStatus = await api.get('/qr-code/health');
            const data = backendStatus.data || {};
            const isHealthy = data.status === 'healthy' || data.data?.service === 'healthy';

            return {
                frontend: {
                    available: frontendAvailable,
                    library: frontendAvailable ? 'qr-code-styling' : null
                },
                backend: {
                    available: true,
                    ...data
                },
                hybrid: frontendAvailable && isHealthy
            };
        } catch (error) {
            return {
                frontend: { available: false, error: error.message },
                backend: { available: false, error: error.message },
                hybrid: false
            };
        }
    }

    detectContentType(content) {
        if (!content || typeof content !== 'string') {
            return 'text';
        }

        const trimmedContent = content.trim();

        // Empty content
        if (!trimmedContent) {
            return 'text';
        }

        // vCard detection (most specific first)
        if (trimmedContent.startsWith('BEGIN:VCARD') && trimmedContent.includes('END:VCARD')) {
            return 'vcard';
        }

        // WiFi detection
        if (trimmedContent.startsWith('WIFI:')) {
            return 'wifi';
        }

        // SMS detection (tel: or sms: schemes)
        if (trimmedContent.startsWith('sms:') || trimmedContent.startsWith('SMS:')) {
            return 'sms';
        }

        // Phone number detection (tel: scheme or phone patterns)
        if (trimmedContent.startsWith('tel:') || trimmedContent.startsWith('TEL:')) {
            return 'sms';
        }

        // Email detection (mailto: scheme)
        if (trimmedContent.startsWith('mailto:') || trimmedContent.startsWith('MAILTO:')) {
            return 'email';
        }

        // Location detection (geo: scheme)
        if (trimmedContent.startsWith('geo:')) {
            return 'location';
        }

        // URL detection (try to parse as URL)
        try {
            const url = new URL(trimmedContent);
            // Valid URL with proper protocol
            if (url.protocol === 'http:' || url.protocol === 'https:' ||
                url.protocol === 'ftp:' || url.protocol === 'ftps:') {
                return 'url';
            }
        } catch (e) {
            // Not a valid URL, continue checking
        }

        // URL detection without protocol (common patterns)
        const urlPatterns = [
            /^(www\.)[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}(\/.*)?$/i,
            /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.(com|org|net|edu|gov|mil|int|co|io|ly|me|tv|info|biz|name|mobi|pro|travel|museum|aero|coop|jobs|cat|asia|tel|xxx|tk|ml|ga|cf|eu|us|uk|ca|au|jp|cn|de|fr|it|es|ru|br|in|mx|nl|se|no|dk|fi|pl|cz|sk|hu|ro|bg|hr|si|ee|lv|lt|mt|cy|lu|be|at|ch|li|is|ie|pt|gr|tr|il|ae|sa|eg|za|ng|ke|gh|tz|ug|mw|zm|zw|bw|na|sz|ls|mg|mu|sc|km|dj|so|et|er|sd|ly|td|cf|cm|gq|ga|cg|cd|ao|st|cv|gw|gn|sl|lr|ci|bf|ml|ne|sn|gm|mr|ma|dz|tn|eh|ps|jo|sy|lb|iq|ir|af|pk|bd|np|bt|lk|mv|mm|th|la|kh|vn|my|sg|bn|id|ph|tw|kr|kp|mn|hk|mo|jp|au|nz|pg|fj|sb|vu|nc|pf|ck|nu|to|ws|ki|tv|nr|pw|fm|mh|mp|gu|as|vi|pr)(\/.*)?(:\d+)?(\/.*)?$/i
        ];

        for (const pattern of urlPatterns) {
            if (pattern.test(trimmedContent)) {
                return 'url';
            }
        }

        // Email detection (standalone email addresses)
        const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (emailPattern.test(trimmedContent)) {
            return 'email';
        }

        // Phone number detection (various international formats)
        const phonePatterns = [
            /^\+?[1-9]\d{1,14}$/, // E.164 format
            /^\+?1[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/, // US format
            /^\+?44[-.\s]?[0-9]{4}[-.\s]?[0-9]{6}$/, // UK format
            /^\+?33[-.\s]?[0-9][-.\s]?[0-9]{8}$/, // French format
            /^\+?49[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{7,8}$/, // German format
            /^\+?81[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{4}[-.\s]?[0-9]{4}$/, // Japanese format
            /^(\+?\d{1,3}[-.\s]?)?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/, // Generic international
            /^\(\d{3}\)[-.\s]?\d{3}[-.\s]?\d{4}$/, // US with parentheses
            /^\d{3}[-.\s]?\d{3}[-.\s]?\d{4}$/, // US simple format
            /^\d{10,15}$/ // Simple digit sequence (10-15 digits)
        ];

        for (const pattern of phonePatterns) {
            if (pattern.test(trimmedContent)) {
                return 'sms';
            }
        }

        // Location detection (coordinate patterns)
        const locationPatterns = [
            /^-?\d+\.?\d*,-?\d+\.?\d*$/, // Simple lat,lng
            /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/, // Lat, lng with spaces
            /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)/, // More flexible coordinate matching
            /^([-+]?(?:[1-8]?\d(?:\.\d+)?|90(?:\.0+)?)),\s*([-+]?(?:180(?:\.0+)?|(?:(?:1[0-7]\d)|(?:[1-9]?\d))(?:\.\d+)?))$/ // Precise lat/lng validation
        ];

        for (const pattern of locationPatterns) {
            if (pattern.test(trimmedContent)) {
                return 'location';
            }
        }

        // Special protocol detection
        const protocolPatterns = [
            { pattern: /^skype:/i, type: 'url' },
            { pattern: /^whatsapp:/i, type: 'url' },
            { pattern: /^telegram:/i, type: 'url' },
            { pattern: /^viber:/i, type: 'url' },
            { pattern: /^signal:/i, type: 'url' },
            { pattern: /^discord:/i, type: 'url' },
            { pattern: /^slack:/i, type: 'url' },
            { pattern: /^zoom:/i, type: 'url' },
            { pattern: /^teams:/i, type: 'url' },
            { pattern: /^spotify:/i, type: 'url' },
            { pattern: /^apple-music:/i, type: 'url' },
            { pattern: /^youtube:/i, type: 'url' },
            { pattern: /^netflix:/i, type: 'url' },
            { pattern: /^uber:/i, type: 'url' },
            { pattern: /^lyft:/i, type: 'url' },
            { pattern: /^venmo:/i, type: 'url' },
            { pattern: /^paypal:/i, type: 'url' },
            { pattern: /^cashapp:/i, type: 'url' },
            { pattern: /^bitcoin:/i, type: 'url' },
            { pattern: /^ethereum:/i, type: 'url' }
        ];

        for (const { pattern, type } of protocolPatterns) {
            if (pattern.test(trimmedContent)) {
                return type;
            }
        }

        // Social media handle detection
        const socialPatterns = [
            /^@[a-zA-Z0-9_]+$/, // Twitter/X handle
            /^#[a-zA-Z0-9_]+$/, // Hashtag
            /^[a-zA-Z0-9_]+$/   // Simple username (could be social media)
        ];

        for (const pattern of socialPatterns) {
            if (pattern.test(trimmedContent) && trimmedContent.length > 2) {
                return 'text'; // Keep as text but could be social media
            }
        }

        // Check for structured data indicators
        if (trimmedContent.includes('BEGIN:') || trimmedContent.includes('VERSION:')) {
            return 'vcard'; // Might be an incomplete vCard
        }

        if (trimmedContent.includes('T:') || trimmedContent.includes('S:') || trimmedContent.includes('P:')) {
            return 'wifi'; // Might be an incomplete WiFi string
        }

        // Default to text for everything else
        return 'text';
    }
}

export default new QRService();